import React, { useEffect, useState } from 'react';

import { Button, Drawer, message, Popconfirm, Space } from 'antd';
import { TableHeader } from 'components/TableHeader';
import { FETCH_ERROR } from 'constants/alerts';
import { RecordFields } from 'components/RecordFields';
import { useDrawers } from 'hooks/useDrawers';
import { usePagination } from 'hooks/usePagination';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { UserService } from 'services/UserService';
import { boolToText } from 'utils';
import { RolesService } from 'services/RolesService';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { TribesService } from 'services/TribesService';
import { AccessControl } from 'components/AccessControl';
import { UsersTable } from './UsersTable';
import { UsersForm } from './UsersForm';

function Users() {
  useDocumentTitle('Пользователи');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectItems, setSelectItems] = useState({
    roles: [],
    tribes: [],
  });
  const { setCount } = useHeaderChanger('Пользователи');

  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();

  const { page, perPage, totalRows, setPagination, setTotalRows } =
    usePagination();

  useEffect(() => {
    fetchRolesItems();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [page, perPage]);

  const fetchRolesItems = async () => {
    try {
      const fetchRoles = RolesService.getRoles(1, 100);
      const fetchTribes = TribesService.getTribes(1, 100);

      const [rolesData, tribesData] = await Promise.all([
        fetchRoles,
        fetchTribes,
      ]);

      const roles = rolesData.items;
      const tribes = tribesData.items;

      setSelectItems({
        roles,
        tribes,
      });
    } catch (err) {
      message.error(FETCH_ERROR);
    }
  };

  const fetchUsers = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const users = await UserService.getUsers(page, perPage);

      if (users) {
        setUsers(users.items);
        setTotalRows(users.total);
        setCount(users.total);
      }
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {};

  return (
    <>
      <TableHeader onCreate={openCreateForm} pageId="users" />

      <UsersTable
        items={users}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        onRowClick={openDrawer}
        onChange={setPagination}
      />

      <Drawer
        title={activeRecord?.full_name}
        width="50%"
        visible={drawerVisible}
        onClose={closeDrawer}
        extra={
          <Space>
            <AccessControl pageId="users" action="update">
              <Button
                type="default"
                icon={<EditOutlined />}
                onClick={() => {
                  openEditForm();
                }}
              />
            </AccessControl>

            <AccessControl pageId="users" action="delete">
              <Popconfirm
                title="Вы действительно хотите удалить пользователя?"
                placement="bottomLeft"
                onConfirm={handleDelete}
                okText="Да"
                cancelText="Нет"
              >
                <Button danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </AccessControl>
          </Space>
        }
      >
        {activeRecord && (
          <RecordFields
            record={activeRecord}
            fields={[
              {
                label: 'ИИН/БИН',
                key: 'bin_iin',
              },
              {
                label: 'ФИО',
                key: 'full_name',
              },
              {
                label: 'Эл. почта',
                key: 'email',
              },
              {
                label: 'Тел. номер',
                key: 'phone',
              },
              {
                label: 'Роль',
                key: 'roles',
                computedValue: activeRecord?.roles
                  .map((item) => item.name)
                  .join(', '),
              },
              {
                label: 'Трайб',
                key: 'tribes',
                computedValue: activeRecord?.tribes
                  .map((item) => item.title)
                  .join(', '),
              },
              {
                label: 'Сотрудник',
                key: 'is_staff',
                computedValue: boolToText(activeRecord?.is_staff),
              },
              {
                label: 'Администратор',
                key: 'is_superuser',
                computedValue: boolToText(activeRecord?.is_superuser),
              },
            ]}
          />
        )}
      </Drawer>

      <UsersForm
        selectItems={selectItems}
        isActive={formIsActive}
        initialValues={activeRecord}
        fetchData={fetchUsers}
        onClose={closeForm}
      />
    </>
  );
}

export { Users };
